import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import { DonateFormType } from "@components/donate/DonateV3/types";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { NO_TIPPING } from "@every.org/common/src/entity/constants";
import {
  DonationFlowPaymentOption,
  DonationFrequency,
} from "@every.org/common/src/entity/types";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { useUserExperiment } from "src/utility/abtesting";

const DEFAULT_COVER_COSTS_PERCENT = 4;

function isCoverCostsPaymentOption(
  option: DonationFlowPaymentOption | undefined
): boolean {
  return [
    DonationFlowPaymentOption.CREDIT_CARD,
    DonationFlowPaymentOption.PAYPAL,
    DonationFlowPaymentOption.PAYMENT_REQUEST,
    DonationFlowPaymentOption.VENMO,
  ].includes(option as DonationFlowPaymentOption);
}

export function useCoverCostsMode({
  form,
  formContext,
  nonprofit,
}: {
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  nonprofit: ContextNonprofit;
}) {
  const frequency = form.watch("frequency");

  const shouldSkipCoverCostsExperiment =
    frequency !== DonationFrequency.ONCE ||
    !isCoverCostsPaymentOption(formContext.currentPaymentOption) ||
    NO_TIPPING.includes(nonprofit.id) ||
    !!nonprofit.metadata?.disableTipping;

  const coverCostsExperiment = useUserExperiment(
    "cover_costs",
    shouldSkipCoverCostsExperiment
  );

  const coverCostsUiGroup = coverCostsExperiment.get<string>("ui", "control");
  const coverCostsUiVariant =
    coverCostsUiGroup === "checkbox" || coverCostsUiGroup === "edit"
      ? coverCostsUiGroup
      : "control";
  const coverCostsPercent = coverCostsExperiment.get<number>(
    "percent",
    DEFAULT_COVER_COSTS_PERCENT
  );

  useEffect(() => {
    if (shouldSkipCoverCostsExperiment) {
      formContext.setIsCoverCostsMode(false);
      return;
    }
    formContext.setIsCoverCostsMode(coverCostsUiVariant !== "control");
    return () => {
      formContext.setIsCoverCostsMode(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverCostsUiVariant, shouldSkipCoverCostsExperiment]);

  return {
    coverCostsUiVariant,
    coverCostsPercent,
    isCoverCostsMode: formContext.isCoverCostsMode,
  };
}
